import { ComponentChildren, Ref } from 'preact';

import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { Flex } from '../UNSAFE_Flex';
import { classNames } from '../utils/UNSAFE_classNames';
import { TextFieldStyles, TextFieldVariantOptions } from './themes/TextFieldStyles.css';
import { useInputGroupContext } from '../hooks/UNSAFE_useInputGroupContext';
import { useUser } from '../hooks/UNSAFE_useUser';
import { TextFieldRedwoodTheme } from './themes/redwood/TextFieldTheme';

type VariantProps = {
  contentVariant?: 'input' | 'textarea';
  statusVariant?: 'error' | 'none' | 'warning';
  styleVariant?: 'default' | 'embedded';
};

type Props = VariantProps & {
  mainContent?: ComponentChildren;
  insideLabel?: ComponentChildren;
  startContent?: ComponentChildren;
  endContent?: ComponentChildren;
  resize?: 'horizontal' | 'vertical' | 'both';
  rootRef?: Ref<HTMLDivElement>;
  hasZeroStartMargin?: boolean;
};

// Renders the oj-text-field-container dom which includes the
// start, middle (where the inside label and inputElem goes),
// and end pieces of the form component.
// This does not include user assistance because that is rendered outside the
// oj-text-field-container; that is rendered in the TextField component.
export const TextFieldContent = ({
  contentVariant = 'input',
  insideLabel,
  mainContent,
  startContent,
  statusVariant = 'none',
  styleVariant = 'default',
  endContent,
  resize,
  rootRef,
  hasZeroStartMargin
}: Props) => {
  const { isDisabled, isFocused, isLoading } = useFormFieldContext();
  const { direction } = useUser();
  const isLtr = direction === 'ltr';
  const hasInsideLabel = insideLabel !== undefined;
  const inputGroupContext = useInputGroupContext();
  const hasGroupContext = inputGroupContext !== null;
  // depending on browser direction, apply the appropriate styling.
  // when 'rtl' the flex row direction is reversed.
  const inputGroupPosition = inputGroupContext
    ? inputGroupContext.last
      ? isLtr
        ? 'groupRight'
        : 'groupLeft'
      : inputGroupContext.index === 0
      ? isLtr
        ? 'groupLeft'
        : 'groupRight'
      : 'groupMiddle'
    : undefined;

  const {
    variantClasses,
    styles: { textFieldContainerBase }
  } = useComponentTheme<TextFieldVariantOptions, TextFieldStyles>(TextFieldRedwoodTheme, {
    statusVariant,
    styleVariant,
    contentVariant,
    insideLabel: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel',
    focused: isFocused ? 'isFocused' : 'notFocused',
    disabled: isDisabled ? 'isDisabled' : 'notDisabled',
    loading: isLoading ? 'isLoading' : 'notLoading',
    resize: resize ?? 'none',
    withinGroup: hasGroupContext ? 'isWithinGroup' : undefined,
    inputGroupPosition
  });

  const { variantClasses: startContentStyles } = useComponentTheme<TextFieldVariantOptions>(
    TextFieldRedwoodTheme,
    {
      startContent: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel',
      startContentMargin: hasZeroStartMargin ? 'noStartMargin' : 'hasStartMargin',
      startContentDisabled: isDisabled ? 'isDisabled' : 'notDisabled'
    }
  );
  const { variantClasses: middleStyles } = useComponentTheme<TextFieldVariantOptions>(
    TextFieldRedwoodTheme,
    { middleContent: contentVariant }
  );
  const { variantClasses: endContentStyles } = useComponentTheme<TextFieldVariantOptions>(
    TextFieldRedwoodTheme,
    { endContent: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel' }
  );

  return (
    <div
      role="presentation"
      class={classNames([variantClasses, textFieldContainerBase])}
      ref={rootRef}>
      {startContent && (
        <span class={startContentStyles}>
          <Flex justify="center" align="center" hasZeroMargins={true}>
            {startContent}
          </Flex>
        </span>
      )}
      <div class={middleStyles}>
        {insideLabel}
        {mainContent}
      </div>
      {endContent && (
        <span class={endContentStyles}>
          <Flex justify="center" align="center" hasZeroMargins={true}>
            {endContent}
          </Flex>
        </span>
      )}
    </div>
  );
};
